// Package imports:
import React, { useEffect, useMemo, useState } from "react";
// Component imports:
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import Table, { IColumn } from "../../ui-elements/Table/Table";
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import Loading from "../../ui-elements/Loading/Loading";
import Alert from "../../ui-elements/Alert/Alert";
import Link from "../../ui-elements/Link/Link";
import Label from "../../ui-elements/Label/Label";
import Button from "../../ui-elements/Button/Button";
import Tooltip from "../../ui-elements/Tooltip/Tooltip";
import PepWatchSanctionsConnectionModal from "./PepWatchModals/PepWatchSanctionsConnectionModal";
import PepWatchPEPConnectionModal from "./PepWatchModals/PepWatchPEPConnectionModal";
// Service imports:
import { getTableDate, insertDash, isSSN } from "../../services/utils";
import { PaginationDropdown, PaginationFlipper, usePagination } from "../../services/paginationHooks";
// Type imports:
import {
  CompanyWatchItem,
  CompanyEventsData,
  AdilarRole,
  ADILAR_ROLE_LABELS,
  CompanyChangeEvent,
  CompanyWatchChangeEvent,
  CompanyStatusUpdateEvent,
  IndividualWatchChangeEvent,
  CompanyWatchLifecycleEvent,
} from "../../types/CompanyWatchTypes";
import {
  fetchCompanyNameByKennitala,
  fetchCompanyWatchItem,
  fetchCompanyWatchEvents,
  fetchIndividualsForCompany,
} from "./CompanyWatchHelpers/CompanyWatchApi";
import { PepWatchListData } from "../../types/PepTypes";

// ============================================================================
// HELPERS
// ============================================================================

const translateFrequency = (frequency: string | null | undefined): string => {
  if (!frequency) return "Aldrei";
  const translations: Record<string, string> = {
    daily: "Daglega",
    never: "Aldrei",
    quarterly: "Ársfjórðungslega",
    weekly: "Vikulega",
    monthly: "Mánaðarlega",
    yearly: "Árlega",
  };
  return translations[frequency] || frequency;
};

const translateEventType = (eventType: string): string => {
  const translations: Record<string, string> = {
    company_change: "Breyting á lögaðila",
    watch_change: "Breyting á vöktun",
    company_watch_change: "Breyting á vöktun",
    status_update: "Stöðubreyting",
    individual_status_change: "Stöðubreyting",
    individual_change: "Breyting á einstaklingi",
    individual_watch_change: "Breyting á einstaklingi",
    company_watch_start: "Vöktun hafin",
    company_watch_stop: "Vöktun stöðvuð",
  };
  return translations[eventType] || eventType;
};

// Translate known field keys into human-readable Icelandic labels
const FIELD_KEY_LABELS: Record<string, string> = {
  // Company registry changes (from rsk2.fyrirtaeki_breytingar)
  TIN: "Skatthverfi",
  UTIBU: "Útibú",
  FLOKKUR: "Flokkur",
  HLUTAFE: "Hlutafé",
  VT_NAFN: "Varanafn",
  NAFNVERD: "Nafnverð",
  POSTFANG: "Póstfang",
  SAMRUNAR: "Samrunar",
  ADAL_ISAT: "Aðal ÍSAT flokkur",
  AUKA_ISAT: "Auka ÍSAT flokkur",
  KENNITALA: "Kennitala",
  POSTNUMER: "Póstnúmer",
  TILGANGUR: "Tilgangur",
  ATHUGASEMD: "Athugasemd",
  DAGS_SKRAD: "Skráð dagsetning",
  FULLTHEITI: "Fullt heiti",
  LOGHEIMILI: "Lögheimili",
  SKIPTINGAR: "Skiptingar",
  STYTTHEITI: "Stutt heiti",
  BANNMERKING: "Bannmerking",
  FIRMAD_RITA: "Firmaritun",
  HLUTAFE_ISK: "Hlutafé (ISK)",
  DAGS_STOFNAD: "Stofnað dagsetning",
  REKSTRARFORM: "Rekstrarform",
  VT_KENNITALA: "Varanafn kennitala",
  ER_BROTTFELLT: "Er brottfellt",
  ID_FIRMARITUN: "Auðkenni firmaritunar",
  AFSKRAD_TEGUND: "Afskráningartegund",
  BIRTATHUGASEMD: "Birtingarathugasemd",
  DAGS_GJALDTROT: "Dags. gjaldþrots",
  ERLENTAUKHEITI: "Erlent aukaheiti",
  KT_MODURFELAGS: "Kennitala móðurfélags",
  DAGS_BREYTINGAR: "Dags. breytinga",
  DAGS_BROTTFELLT: "Dags. brottfellingar",
  DAGS_SAMTHYKKTA: "Dags. samþykkta",
  PNR_LOGHEIMILIS: "Póstnúmer lögheimilis",
  DAGS_STJBRFUNDUR: "Dags. stjórnarbreytingarfundar",
  DAGS_SIDASTBREYTT: "Síðast breytt",
  SVEITAFELAGSNUMER: "Sveitarfélagsnúmer",
  ERLENTHEIMILISFANG: "Erlent heimilisfang",
  JSON_STADA_VERSION: "Stöðuútgáfa",
  DAGS_STJBRTILKYNNING: "Dags. stjórnarbreytingartilkynningar",
  GJALDMIDILL_HLUTAFJAR: "Gjaldmiðill hlutafjár",
  ERLENTHEIMILISFANGLAND: "Land (erlent heimilisfang)",
  HOMLUR_A_MEDFERD_HLUTA: "Hömlur á meðferð hluta",
  LAUSNARSKYLDA_A_HLUTUM: "Lausnarskylda á hlutum",
  ERLENTHEIMILISFANGFYLKI: "Fylki (erlent heimilisfang)",
  ERLENTHEIMILISFANGSTADUR: "Staður (erlent heimilisfang)",
  SVEITAFELAGSNR_LOGHEIMILIS: "Sveitarfélagsnr. lögheimilis",

  // Watch config changes
  AdilarIVoktun: "Aðilar í vöktun",
  PepFrequency: "PEP tíðni",
  SanctionsFrequency: "Válistar tíðni",

  // Individual watch changes
  CompanyPepFrequency: "PEP tíðni (frá lögaðila)",
  CompanySanctionsFrequency: "Válistar tíðni (frá lögaðila)",

  // Status changes
  PepStatus: "Staða á PEP lista",
  SanctionsStatus: "Staða á válistum",
};

// Translate PersonChangeEvent role values (PascalCase, different from adilarIVoktun enum)
const PERSON_CHANGE_ROLE_LABELS: Record<string, string> = {
  RaunverulegurEigandi: "Raunverulegur eigandi",
  "Stjórn": "Stjórn",
  "Prókúruhafi": "Prókúruhafi",
  "Framkvæmdastjórn": "Framkvæmdastjórn",
};

const translatePersonChangeRole = (role: string): string =>
  PERSON_CHANGE_ROLE_LABELS[role] ?? ADILAR_ROLE_LABELS[role as AdilarRole] ?? role;

const translateFieldKey = (key: string): string => FIELD_KEY_LABELS[key] ?? key;

// Try to detect an ISO date string and render it as a readable date
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?Z?)?$/;
const tryFormatDate = (raw: string): string => {
  if (!raw) return raw;
  if (ISO_DATE_RE.test(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return getTableDate(d, "DD/MM/YYYY", ".");
  }
  return raw;
};

// Translate known enum-ish values
const translateValue = (key: string, value: string): string => {
  // Frequencies
  if (
    key === "PepFrequency" ||
    key === "SanctionsFrequency" ||
    key === "CompanyPepFrequency" ||
    key === "CompanySanctionsFrequency"
  ) {
    return translateFrequency(value);
  }
  // Roles
  if (key === "AdilarIVoktun") {
    return ADILAR_ROLE_LABELS[value as AdilarRole] ?? value;
  }
  // Date-like
  return tryFormatDate(value);
};

// Truncate a long string with a tooltip showing the full text
const renderLongText = (text: string, limit = 60): React.ReactNode => {
  if (!text || text.length <= limit) return text || "-";
  return (
    <Tooltip
      normalText
      text={<span>{text.slice(0, limit)}…</span>}
      tooltip={<p>{text}</p>}
    />
  );
};

// Render a "old → new" diff with values translated individually
const renderValueDiff = (
  key: string,
  oldValue: string[] | undefined,
  newValue: string[] | undefined
): React.ReactNode => {
  const oldArr = oldValue ?? [];
  const newArr = newValue ?? [];

  // Multi-value (like AdilarIVoktun): show as added/removed badges
  if (oldArr.length > 1 || newArr.length > 1) {
    const oldSet = new Set(oldArr);
    const newSet = new Set(newArr);
    const removed = oldArr.filter((v) => !newSet.has(v));
    const added = newArr.filter((v) => !oldSet.has(v));
    if (removed.length === 0 && added.length === 0) {
      return (
        <span className="value-diff">
          {newArr.map((v) => translateValue(key, v)).join(", ") || "-"}
        </span>
      );
    }
    return (
      <div className="value-diff value-diff--multi">
        {removed.length > 0 && (
          <div>
            <span className="value-diff__label value-diff__label--removed">Fjarlægt:</span>{" "}
            {removed.map((v) => translateValue(key, v)).join(", ")}
          </div>
        )}
        {added.length > 0 && (
          <div>
            <span className="value-diff__label value-diff__label--added">Bætt við:</span>{" "}
            {added.map((v) => translateValue(key, v)).join(", ")}
          </div>
        )}
      </div>
    );
  }

  // Single value: old → new
  const oldStr = oldArr[0] ?? "-";
  const newStr = newArr[0] ?? "-";
  return (
    <span className="value-diff">
      <span className="value-diff__old">{renderLongText(translateValue(key, oldStr))}</span>{" "}
      <span className="value-diff__arrow">→</span>{" "}
      <span className="value-diff__new">{renderLongText(translateValue(key, newStr))}</span>
    </span>
  );
};

// Collapse consecutive duplicate individual_watch_change events
const collapseSpammyEvents = <T extends { eventType: string; eventTimestamp: string }>(
  events: T[]
): T[] => {
  const out: T[] = [];
  const seenIndividualChanges = new Set<string>();
  for (const ev of events) {
    if (
      ev.eventType === "individual_watch_change" ||
      ev.eventType === "individual_change"
    ) {
      const e = ev as any;
      // Signature: per person per key per (old→new) pair. Deduplicate across the whole feed.
      const sig = (e.valueChanges ?? [])
        .map(
          (vc: any) =>
            `${vc.kennitala}|${vc.key}|${(vc.oldValue ?? []).join(",")}→${(vc.newValue ?? []).join(",")}`
        )
        .join(";");
      if (seenIndividualChanges.has(sig)) continue;
      seenIndividualChanges.add(sig);
    }
    out.push(ev);
  }
  return out;
};

type UnifiedEvent =
  | CompanyChangeEvent
  | CompanyWatchChangeEvent
  | CompanyStatusUpdateEvent
  | IndividualWatchChangeEvent
  | CompanyWatchLifecycleEvent;

const flattenEvents = (data: CompanyEventsData | null): UnifiedEvent[] => {
  if (!data) return [];
  const all: UnifiedEvent[] = [
    ...(data.companyChangeEvents ?? []),
    ...(data.companyWatchChangeEvents ?? []),
    ...(data.statusUpdateEvents ?? []),
    ...(data.individualWatchChangeEvents ?? []),
    ...(data.lifecycleEvents ?? []),
  ];
  // Sort by eventTimestamp desc
  return all.sort(
    (a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime()
  );
};

// ============================================================================
// DATA HOOKS
// ============================================================================

const useCompanyName = (id: string) => {
  const [name, setName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    const fetchName = async () => {
      try {
        setIsLoading(true);
        const result = await fetchCompanyNameByKennitala(id);
        setName(result);
      } finally {
        setIsLoading(false);
      }
    };
    fetchName();
  }, [id]);

  return { name, isLoading };
};

const useCompanyWatchItemData = (id: string, reloadKey: number) => {
  const [item, setItem] = useState<CompanyWatchItem | Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    const fetchItem = async () => {
      try {
        setIsLoading(true);
        const result = await fetchCompanyWatchItem(id);
        setItem(result);
      } catch (e) {
        setItem(e instanceof Error ? e : new Error("Óþekkt villa"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchItem();
  }, [id, reloadKey]);

  return { item, isLoading };
};

const useCompanyIndividuals = (id: string, reloadKey: number) => {
  const [individuals, setIndividuals] = useState<PepWatchListData[] | Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await fetchIndividualsForCompany(id);
        setIndividuals(result);
      } catch (e) {
        setIndividuals(e instanceof Error ? e : new Error("Óþekkt villa"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, reloadKey]);

  return { individuals, isLoading };
};

const useCompanyEvents = (id: string) => {
  const [events, setEvents] = useState<CompanyEventsData | Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        const result = await fetchCompanyWatchEvents(id);
        setEvents(result);
      } catch (e) {
        setEvents(e instanceof Error ? e : new Error("Óþekkt villa"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchEvents();
  }, [id]);

  return { events, isLoading };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const CompanyInfoSection: React.FC<{
  id: string;
  name: string | null;
  isLoading: boolean;
}> = ({ id, name, isLoading }) => {
  return (
    <div className="info-col-wrapper">
      <div className="info-col">
        <div className="info-item">
          <span className="title">Nafn lögaðila</span>
          <span className="value">{isLoading ? <Loading width="150px" count={1} /> : name ?? "Óþekkt"}</span>
        </div>
      </div>
      <div className="info-col">
        <div className="info-item">
          <span className="title">Kennitala</span>
          <span className="value">{insertDash(id)}</span>
        </div>
      </div>
    </div>
  );
};

const WatchSummarySection: React.FC<{
  item: CompanyWatchItem | Error | null;
  isLoading: boolean;
}> = ({ item, isLoading }) => {
  if (isLoading) return <Loading />;
  if (item instanceof Error) return <Alert type="error" headText={item.message} />;
  if (!item) return <Alert type="alert" headText="Lögaðili fannst ekki í vöktun" />;

  const statusColumns: IColumn<{ voktun: string; tidni: string }>[] = [
    {
      title: "Vöktun",
      renderCell: (d) => d.voktun,
      textAlign: "left",
    },
    {
      title: "Tíðni",
      renderCell: (d) => d.tidni,
      textAlign: "left",
    },
  ];

  const rows = [
    {
      voktun: "Stjórnmálaleg tengsl (PEP)",
      tidni: translateFrequency(item.pepFrequency),
    },
    {
      voktun: "Alþjóðlegir válistar",
      tidni: translateFrequency(item.sanctionsFrequency),
    },
  ];

  return <Table tableSize="lg" data={rows} columns={statusColumns} showEmptyTableText />;
};

const ConnectionsBreakdownSection: React.FC<{
  item: CompanyWatchItem | Error | null;
  isLoading: boolean;
}> = ({ item, isLoading }) => {
  if (isLoading) return <Loading />;
  if (item instanceof Error || !item) return null;

  return (
    <div className="info-col-wrapper">
      <div className="info-col">
        <div className="info-item">
          <span className="title">Fjöldi tengdra</span>
          <span className="value">{item.connectionCount ?? 0}</span>
        </div>
      </div>
      <div className="info-col">
        <div className="info-item">
          <span className="title">Fjöldi á PEP lista</span>
          <span className="value">{item.pepCount ?? 0}</span>
        </div>
      </div>
      <div className="info-col">
        <div className="info-item">
          <span className="title">Fjöldi á válistum</span>
          <span className="value">{item.sanctionsCount ?? 0}</span>
        </div>
      </div>
      <div className="info-col">
        <div className="info-item">
          <span className="title">Fjöldi á listum</span>
          <span className="value">{item.pepOrSanctionsCount ?? 0}</span>
        </div>
      </div>
    </div>
  );
};

const RolesSection: React.FC<{
  item: CompanyWatchItem | Error | null;
  isLoading: boolean;
}> = ({ item, isLoading }) => {
  if (isLoading) return <Loading />;
  if (item instanceof Error || !item) return null;

  const roles = item.adilarList ?? [];
  if (roles.length === 0) return <p>Engir aðilar í vöktun</p>;

  return (
    <p className="pep-roles-list">
      {roles.map((role) => ADILAR_ROLE_LABELS[role as AdilarRole] ?? role).join(", ")}
    </p>
  );
};

const IndividualStatusSection: React.FC<{
  individuals: PepWatchListData[] | Error | null;
  isLoading: boolean;
}> = ({ individuals, isLoading }) => {
  const [pepModalOpen, setPepModalOpen] = useState(false);
  const [pepModalData, setPepModalData] = useState<string | undefined>(undefined);
  const [sanctionsModalOpen, setSanctionsModalOpen] = useState(false);
  const [sanctionsModalData, setSanctionsModalData] = useState<string | undefined>(undefined);

  if (isLoading) return <Loading />;
  if (individuals instanceof Error) return <Alert type="error" headText={individuals.message} />;
  if (!individuals || individuals.length === 0) {
    return <p>Engir tengdir einstaklingar</p>;
  }

  const columns: IColumn<PepWatchListData>[] = [
    {
      title: "Nafn",
      renderCell: ({ personFirstName, personMiddleName, personLastName }) =>
        `${personFirstName ?? ""}${personMiddleName ? " " + personMiddleName : ""}${personLastName ? " " + personLastName : ""}`.trim() || "-",
      textAlign: "left",
    },
    {
      title: "Kennitala",
      renderCell: ({ id: { personId } }) => insertDash(personId),
      textAlign: "left",
    },
    {
      title: "Stjórnmálaleg tengsl (PEP)",
      renderCell: (item) => {
        if (!isSSN(item.id.personId)) return "Ógild kennitala";
        if (item.pepFrequency === "never") return "-";
        if (!item.lastPepCheckTimestamp) return "Í bið";
        return (
          <div className="pep-status-cell">
            <Tooltip
              noUnderline
              text={
                <Label labelType={item.pepStatus ? "yes" : "no"} size="lg" text={item.pepStatus ? "Já" : "Nei"} />
              }
              tooltip={
                <p>
                  Síðasta uppfletting:{" "}
                  {item.lastPepCheckTimestamp
                    ? getTableDate(item.lastPepCheckTimestamp, "DD/MM/YYYY", ".", "")
                    : "Óþekkt"}
                </p>
              }
            />
            {item.pepStatus && (
              <Button
                buttonType="secondary"
                size="sm"
                onClick={() => {
                  setPepModalOpen(true);
                  setPepModalData(item.id.personId);
                }}
              >
                Skoða
              </Button>
            )}
          </div>
        );
      },
      textAlign: "left",
    },
    {
      title: "Alþjóðlegir válistar",
      renderCell: (item) => {
        if (item.sanctionsFrequency === "never") return "-";
        if (!item.lastSanctionsCheckTimestamp) return "Í bið";
        return (
          <div className="pep-status-cell">
            <Tooltip
              noUnderline
              text={
                <Label
                  labelType={item.sanctionsStatus ? "yes" : "no"}
                  size="lg"
                  text={item.sanctionsStatus ? "Já" : "Nei"}
                />
              }
              tooltip={
                <p>
                  Síðasta uppfletting:{" "}
                  {item.lastSanctionsCheckTimestamp
                    ? getTableDate(item.lastSanctionsCheckTimestamp, "DD/MM/YYYY", ".", "")
                    : "Óþekkt"}
                </p>
              }
            />
            {((item.openCount ?? 0) > 0 || (item.truePositiveCount ?? 0) > 0 || (item.falsePositiveCount ?? 0) > 0) && (
              <Button
                buttonType="secondary"
                size="sm"
                onClick={() => {
                  setSanctionsModalOpen(true);
                  setSanctionsModalData(item.id.personId);
                }}
              >
                Skoða
              </Button>
            )}
          </div>
        );
      },
      textAlign: "left",
    },
    {
      title: "Síðasta uppfærsla",
      renderCell: (item) => {
        const date = item.timestampUpdated ?? item.lastPepCheckTimestamp ?? item.lastSanctionsCheckTimestamp;
        return date ? getTableDate(new Date(date as any), "DD/MM/YYYY", ".") : "-";
      },
      textAlign: "left",
    },
  ];

  return (
    <>
      <Table tableSize="lg" data={individuals} columns={columns} showEmptyTableText />
      <PepWatchPEPConnectionModal
        show={pepModalOpen}
        close={() => setPepModalOpen(false)}
        id={pepModalData}
      />
      <PepWatchSanctionsConnectionModal
        show={sanctionsModalOpen}
        close={() => setSanctionsModalOpen(false)}
        id={sanctionsModalData}
      />
    </>
  );
};

const EventHistorySection: React.FC<{
  events: CompanyEventsData | Error | null;
  isLoading: boolean;
}> = ({ events, isLoading }) => {
  const unified = useMemo(() => {
    if (!events || events instanceof Error) return [];
    return collapseSpammyEvents(flattenEvents(events));
  }, [events]);

  const pagination = usePagination(unified.length, 10, 0);

  const paginatedData = useMemo(() => {
    return unified.slice(pagination.startIndex, pagination.endIndex);
  }, [unified, pagination.startIndex, pagination.endIndex]);

  if (isLoading) return <Loading />;
  if (events instanceof Error) return <Alert type="error" headText={events.message} />;
  if (unified.length === 0) return <p>Engar breytingar skráðar</p>;

  const renderEventDescription = (event: UnifiedEvent): React.ReactNode => {
    switch (event.eventType) {
      case "company_change": {
        const e = event as CompanyChangeEvent;
        return (
          <div className="event-change-cell">
            {e.valueChanges?.map((vc, i) => (
              <div className="event-change-row" key={`vc-${i}`}>
                <span className="event-change-row__key">{translateFieldKey(vc.key)}:</span>{" "}
                {renderValueDiff(vc.key, vc.oldValue, vc.newValue)}
              </div>
            ))}
            {e.peopleChanges?.map((pc, i) => (
              <div className="event-change-row" key={`pc-${i}`}>
                <strong>{pc.name}</strong>
                {pc.kennitala ? ` (${insertDash(pc.kennitala)})` : ""} —{" "}
                {pc.addedOrRemoved === "added" ? "bætt við" : "fjarlægð"} sem{" "}
                {translatePersonChangeRole(pc.role)}
              </div>
            ))}
          </div>
        );
      }
      case "watch_change":
      case "company_watch_change": {
        const e = event as CompanyWatchChangeEvent;
        return (
          <div className="event-change-cell">
            {e.valueChanges?.map((vc, i) => (
              <div className="event-change-row" key={i}>
                <span className="event-change-row__key">{translateFieldKey(vc.key)}:</span>{" "}
                {renderValueDiff(vc.key, vc.oldValue, vc.newValue)}
              </div>
            ))}
          </div>
        );
      }
      case "status_update":
      case "individual_status_change": {
        const e = event as CompanyStatusUpdateEvent;
        const statusText = (s: boolean | null) =>
          s === null ? "Óþekkt" : s ? "Já" : "Nei";
        return (
          <div className="event-change-cell">
            <div className="event-change-row">
              <strong>{e.name}</strong> ({insertDash(e.personId)}) —{" "}
              <span className="event-change-row__key">{translateFieldKey(e.key)}:</span>{" "}
              <span className="value-diff">
                <span className="value-diff__old">{statusText(e.oldStatus)}</span>{" "}
                <span className="value-diff__arrow">→</span>{" "}
                <span className="value-diff__new">{statusText(e.newStatus)}</span>
              </span>
            </div>
          </div>
        );
      }
      case "company_watch_start":
        return (
          <div className="event-change-cell">
            <div className="event-change-row">Vöktun á lögaðila hófst</div>
          </div>
        );
      case "company_watch_stop":
        return (
          <div className="event-change-cell">
            <div className="event-change-row">Vöktun á lögaðila stöðvuð</div>
          </div>
        );
      case "individual_change":
      case "individual_watch_change": {
        const e = event as IndividualWatchChangeEvent;
        return (
          <div className="event-change-cell">
            {e.valueChanges?.map((vc, i) => (
              <div className="event-change-row" key={i}>
                <strong>{vc.name}</strong> ({insertDash(vc.kennitala)}) —{" "}
                <span className="event-change-row__key">{translateFieldKey(vc.key)}:</span>{" "}
                {renderValueDiff(vc.key, vc.oldValue, vc.newValue)}
              </div>
            ))}
          </div>
        );
      }
      default:
        return "-";
    }
  };

  const columns: IColumn<UnifiedEvent>[] = [
    {
      title: "Dagsetning",
      renderCell: (e) =>
        e.eventTimestamp ? getTableDate(new Date(e.eventTimestamp), "DD/MM/YYYY HH:MM:SS", ".") : "-",
      textAlign: "left",
    },
    {
      title: "Tegund",
      renderCell: (e) => translateEventType(e.eventType),
      textAlign: "left",
    },
    {
      title: "Breyting",
      renderCell: renderEventDescription,
      textAlign: "left",
    },
  ];

  return (
    <>
      <Table
        tableSize="lg"
        data={paginatedData}
        columns={columns}
        showEmptyTableText
        bSide={
          <PaginationDropdown
            paginationConfig={{ usePortal: true }}
            paginationState={pagination}
          />
        }
      />
      <PaginationFlipper paginationState={pagination} />
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ViewPepCompanyWatchReport: React.FC<{ id: string }> = ({ id }) => {
  const [reloadKey] = useState(0);

  const { name, isLoading: isNameLoading } = useCompanyName(id);
  const { item, isLoading: isItemLoading } = useCompanyWatchItemData(id, reloadKey);
  const { individuals, isLoading: isIndividualsLoading } = useCompanyIndividuals(id, reloadKey);
  const { events, isLoading: isEventsLoading } = useCompanyEvents(id);

  return (
    <div className="main KCL_viewPep KCL_viewPepWatchReport KCL_individual-profile assets-report">
      <div className="shell">
        <HeadSection
          breadcrumbs={
            <Link href="/Valistar/Leit#AML-vaktin" icon="back" linkSize="14">
              AML vaktin - yfirlit
            </Link>
          }
          title="AML vaktin - skýrsla lögaðila"
        />

        <CompanyInfoSection id={id} name={name} isLoading={isNameLoading} />

        <DisplayBox
          title="AML vaktin - niðurstöður"
          asideComponent={
            <div className="report-aside-component">
              <div className="italic-text">
                Skýrsla sótt: {getTableDate(new Date(), "DD/MM/YYYY HH:MM:SS", ".")}
              </div>
            </div>
          }
        >
          <div className="view-pep-watch-report__section">
            <h4>Vöktunarstaða</h4>
            <WatchSummarySection item={item} isLoading={isItemLoading} />
          </div>

          <div className="view-pep-watch-report__section">
            <h4>Sundurliðun tenginga</h4>
            <ConnectionsBreakdownSection item={item} isLoading={isItemLoading} />
          </div>

          <div className="view-pep-watch-report__section">
            <h4>Aðilar í vöktun</h4>
            <RolesSection item={item} isLoading={isItemLoading} />
          </div>

          <div className="view-pep-watch-report__section">
            <h4>Staða tengdra einstaklinga</h4>
            <IndividualStatusSection individuals={individuals} isLoading={isIndividualsLoading} />
          </div>

          <div className="view-pep-watch-report__section">
            <h4>Breytingarsaga</h4>
            <EventHistorySection events={events} isLoading={isEventsLoading} />
          </div>
        </DisplayBox>
      </div>

    </div>
  );
};

export default ViewPepCompanyWatchReport;
